import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Lock, ArrowRight, Loader2, ArrowLeft } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLogin() {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("admin_token", res.data.token);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Login | ALIGN&bull;</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ backgroundColor: "#F7F5F0" }}
        data-testid="admin-login-page"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-10">
            <button
              onClick={() => navigate("/")}
              className="font-['Cormorant_Garamond'] text-3xl font-light tracking-tight text-[#1A1C19] hover:text-[#4A6741] transition-colors mb-2 inline-block"
              data-testid="admin-brand-logo"
            >
              ALIGN<span className="text-[#4A6741]">&bull;</span>
            </button>
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#5C635A]">
              Practice Administration
            </p>
          </div>

          <div className="glass-card p-8 md:p-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-full bg-[#4A6741]/10 flex items-center justify-center">
                <Lock size={16} className="text-[#4A6741]" />
              </div>
              <div>
                <h1 className="font-['Cormorant_Garamond'] text-2xl font-normal text-[#1A1C19]">
                  Admin Access
                </h1>
                <p className="text-xs text-[#5C635A] font-light">
                  Enter your password to continue
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#4A6741] mb-2 block">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input-line"
                  placeholder="Enter admin password"
                  required
                  data-testid="admin-password-input"
                />
              </div>

              {error && (
                <p
                  className="text-red-600 text-sm font-light mb-4"
                  data-testid="admin-login-error"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-pill w-full justify-center"
                data-testid="admin-login-button"
              >
                {loading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            </form>
          </div>

          <div className="text-center mt-6">
            <button
              onClick={() => navigate("/")}
              className="text-xs text-[#5C635A] hover:text-[#4A6741] transition-colors inline-flex items-center gap-2"
              data-testid="back-to-site-link"
            >
              <ArrowLeft size={12} />
              Back to site
            </button>
          </div>
        </motion.div>
      </div>
    </>
  );
}
